import { Text } from "@chakra-ui/react";

import { Info, Warning } from "../alert";
import { Link } from "../link";

import { OrderedList, UnorderedList, ListItem } from "ui/list";
import { Strong } from "ui/strong";

type PushPlatform = "ios" | "android";

const platforms: Record<
  PushPlatform,
  {
    name: string;
    card: string;
    otherName: string;
    credentialsHeading: string;
    credentialsAnchor: string;
    testCheck: string;
  }
> = {
  ios: {
    name: "iOS",
    card: "iOS (APNs)",
    otherName: "Android",
    credentialsHeading: "Apple credentials",
    credentialsAnchor: "#collect-your-apple-credentials",
    testCheck:
      "The test includes an image, so it also checks your Notification Service Extension.",
  },
  android: {
    name: "Android",
    card: "Android (FCM)",
    otherName: "iOS",
    credentialsHeading: "Firebase credentials",
    credentialsAnchor: "#collect-your-firebase-credentials",
    testCheck:
      "The test includes an image, so it also checks that rich notifications display.",
  },
};

type PlatformProps = { platform: PushPlatform };

export const PushEventSourceSteps = ({ platform }: PlatformProps) => {
  const { name } = platforms[platform];

  return (
    <>
      <Text textStyle="paragraph">
        The SDK sends device registrations and notification opens to a
        Hightouch event source of type {name}. Skip this section if your
        workspace already has one.
      </Text>
      <OrderedList>
        <ListItem>
          Go to <Strong>Event Collection &gt; Event sources</Strong> and click{" "}
          <Strong>Create source</Strong>.
        </ListItem>
        <ListItem>
          Click <Strong>{name}</Strong> and finish creating the source.
        </ListItem>
        <ListItem>
          On the source&apos;s <Strong>Setup</Strong> tab, copy the{" "}
          <Strong>Write key</Strong> and the <Strong>API Host</Strong>.
        </ListItem>
      </OrderedList>
      <Text textStyle="paragraph">
        A workspace can have only one {name} event source for push.
      </Text>
    </>
  );
};

export const PushRegisterAppSteps = ({ platform }: PlatformProps) => {
  const { card, otherName, credentialsHeading, credentialsAnchor } =
    platforms[platform];

  return (
    <>
      <OrderedList>
        <ListItem>
          Go to <Strong>Native Delivery &gt; Settings &gt; Apps</Strong>. If you
          already registered this app for {otherName}, open it and skip to the
          next step. Otherwise, click <Strong>Add</Strong>, enter a{" "}
          <Strong>Name</Strong>, and click <Strong>Add</Strong>. The name
          appears on every notification.
        </ListItem>
        <ListItem>
          On the <Strong>{card}</Strong> card, click <Strong>Enable</Strong>.
        </ListItem>
        <ListItem>
          Enter your{" "}
          <Link href={credentialsAnchor} variant="inline">
            {credentialsHeading}
          </Link>
          , then save.
        </ListItem>
        <ListItem>
          Copy the <Strong>App ID</Strong> from the top of the{" "}
          <Strong>{card}</Strong> card.
        </ListItem>
      </OrderedList>
      <Text textStyle="paragraph">
        Hightouch connects the app to your event source when you enable it.
      </Text>
      <Warning>
        <Text>
          You can&apos;t disable a platform after you enable it. To delete an
          app, contact Hightouch support.
        </Text>
      </Warning>
      <Info>
        <Text>
          The App ID isn&apos;t your app&apos;s package name or bundle ID.
          It&apos;s an ID that Hightouch assigns.
        </Text>
      </Info>
    </>
  );
};

export const PushTestSendSteps = ({ platform }: PlatformProps) => {
  const { testCheck } = platforms[platform];

  return (
    <>
      <Text textStyle="paragraph">
        Run your app on a device and log in so that <code>identify</code> runs,
        then send a test from Hightouch. In both places, <Strong>Validate</Strong>{" "}
        checks that a device is registered for the user ID you enter.
      </Text>
      <Text textStyle="paragraph">
        <Strong>From app settings:</Strong> Go to{" "}
        <Strong>Native Delivery &gt; Settings &gt; Apps</Strong>, open your app,
        and click <Strong>Test</Strong>. Enter your user ID, click{" "}
        <Strong>Validate</Strong>, then click <Strong>Send test</Strong>.{" "}
        {testCheck}
      </Text>
      <Text textStyle="paragraph">
        <Strong>From the campaign editor:</Strong> Go to{" "}
        <Strong>Native Delivery &gt; Campaigns</Strong>, click{" "}
        <Strong>Add campaign</Strong>, and select <Strong>Push</Strong>. Under{" "}
        <Strong>Content</Strong>, click <Strong>Edit</Strong> and write your
        message. On the <Strong>Preview</Strong> tab, click{" "}
        <Strong>Send test</Strong>, enter your user ID, click{" "}
        <Strong>Validate</Strong>, then click <Strong>Send test</Strong>.
      </Text>
      <Text textStyle="paragraph">
        If <Strong>Validate</Strong> finds no device, you can enter a device
        token instead. A test that arrives this way means your push credentials
        work and the device isn&apos;t linked to that user.
      </Text>
    </>
  );
};

export const PushNoActiveDevices = ({ platform }: PlatformProps) => {
  const { name, card } = platforms[platform];

  return (
    <>
      <Text textStyle="paragraph">
        <Strong>Validate</Strong> shows this when no device is registered for
        the user ID. Check that:
      </Text>
      <UnorderedList>
        <ListItem>
          <code>appId</code> is the App ID from your app&apos;s{" "}
          <Strong>{card}</Strong> card.
        </ListItem>
        <ListItem>
          Your app calls <code>identify</code> after the user logs in, with the
          parent model&apos;s primary key.
        </ListItem>
        <ListItem>
          The API host in your SDK setup matches the <Strong>API Host</Strong>{" "}
          on your {name} event source&apos;s <Strong>Setup</Strong> tab.
        </ListItem>
      </UnorderedList>
      <Text textStyle="paragraph">
        After you fix any of these, call <code>identify</code> again on the
        device. To see what your app sends, open your {name} event
        source&apos;s <Strong>Debugger</Strong> tab. After{" "}
        <code>identify</code>, you see a <code>CEP Push Token Events</code>{" "}
        event with your user ID and an <code>app_id</code> that matches your App
        ID.
      </Text>
    </>
  );
};
